// BottomSheet.tsx — generisches Bottom-Sheet für den Mobile-Kalender
// (`/termine`, SP6). Markup-Abstraktion der bewährten `.ct-sheet-*`-Klassen
// (contactRedesign.css). Konsistenz-Review C2: die `.ct-sheet-*`-Regeln werden
// NICHT dupliziert — der Original-Selektor in contactRedesign.css wurde um
// `.cal-rd` erweitert (`.kvp-rd .ct-sheet, .cal-rd .ct-sheet`), sodass die
// gleichen Deklarationen ohne `.kvp-rd`-Root unter `.cal-rd` greifen.
//
// SP6 nutzt das Sheet zweifach: Filter-Sheet (Kundentermin/Austausch) und
// Kalender-Auswahl-Sheet (verfügbare Kalender). Scrim-Klick + Escape schließen.

import React, { useEffect } from "react";
import { REFINED } from "../risikoanalyseStep/tokens";
// Die `.ct-sheet-*`-Regeln leben in contactRedesign.css (auf `.cal-rd`
// mitgespiegelt, C2). Sie sind auf der `/termine`-Route sonst nicht garantiert
// geladen → Side-Effect-Import sichert die Optik des Bottom-Sheets.
import "../redesign/contactRedesign.css";
import { alleDokumente } from "../windowManager/browserFenster/fensterRegister";

export interface BottomSheetProps {
    open: boolean;
    title: string;
    onClose: () => void;
    /** ct-sheet-body-Inhalt. */
    children: React.ReactNode;
    /** Optionaler ct-sheet-foot (z. B. Zurücksetzen/Fertig). */
    footer?: React.ReactNode;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({
    open,
    title,
    onClose,
    children,
    footer,
}) => {
    // Escape schließt; Body-Scroll während des offenen Sheets sperren.
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        alleDokumente.addEventListener("keydown", onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            alleDokumente.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div className="ct-sheet-scrim" onClick={onClose}>
            <div
                className="ct-sheet"
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="ct-sheet-head">
                    <h3>{title}</h3>
                    <button
                        type="button"
                        aria-label="Schließen"
                        onClick={onClose}
                        style={closeBtnStyle}
                    >
                        <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden>
                            <path
                                d="M5 5l10 10M15 5L5 15"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                            />
                        </svg>
                    </button>
                </div>
                <div className="ct-sheet-body">{children}</div>
                {footer && <div className="ct-sheet-foot">{footer}</div>}
            </div>
        </div>
    );
};

const closeBtnStyle: React.CSSProperties = {
    width: 44,
    height: 44,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    background: "transparent",
    color: REFINED.ink2,
    cursor: "pointer",
    borderRadius: 10,
    padding: 0,
    margin: "-8px -8px -8px 0",
};

export default BottomSheet;
